import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import {
  AccountUserService,
  AccountUserWorkspaceRoleModel,
} from '@core/services/account-user.service';
import { AuthService } from '@core/services/auth.service';
import { RoleLookupModel, RoleService } from '@core/services/role.service';
import { UiTranslationService } from '@core/services/ui-translation.service';

@Component({
  selector: 'app-invite-setting-modal',
  templateUrl: './invite-setting-modal.html',
  styleUrl: './invite-setting-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteSettingModal implements OnInit {
  readonly activeModal = inject(NgbActiveModal);
  private readonly authService = inject(AuthService);
  private readonly accountUserService = inject(AccountUserService);
  private readonly roleService = inject(RoleService);
  private readonly translations =
    inject(UiTranslationService).namespace('modals');
  readonly t = this.translations.t;

  @Input() mindMapId: number | null = null;
  @Input() workspaceId: number | null = null;
  @Input() accountId: number | null = null;

  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly users = signal<AccountUserWorkspaceRoleModel[]>([]);
  readonly roles = signal<RoleLookupModel[]>([]);
  readonly query = signal('');
  readonly errorMessage = signal<string | null>(null);
  readonly selectedLinkRoleId = signal<number | null>(null);

  readonly filteredUsers = computed(() => {
    const query = this.query().trim().toLocaleLowerCase();
    const users = this.users();
    if (!query) {
      return users;
    }

    return users.filter((user) => {
      return [
        user.userName ?? '',
        user.email,
        user.workspaceName,
        user.roleName,
      ].some((value) => this.normalizeField(value).includes(query));
    });
  });

  ngOnInit(): void {
    this.loadData();
  }

  onQueryInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.query.set(input?.value ?? '');
  }

  private loadData(): void {
    if (this.loaded() || this.loading()) {
      return;
    }

    const accountId = this.resolveAccountId();
    if (!accountId) {
      this.errorMessage.set(this.translations.tr('share.errorInvalidAccount'));
      this.loaded.set(true);
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    forkJoin({
      users: this.accountUserService.listByAccount(accountId),
      roles: this.roleService.listByAccount(accountId),
    })
      .pipe(
        catchError((error: Error) => {
          this.users.set([]);
          this.roles.set([]);
          this.errorMessage.set(
            error?.message ?? this.translations.tr('share.errorLoadUsersRoles'),
          );
          this.selectedLinkRoleId.set(null);
          return of(null);
        }),
        finalize(() => {
          this.loading.set(false);
          this.loaded.set(true);
        }),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        const orderedUsers = [...result.users].sort((a, b) => {
          const byUser = (a.userName ?? a.email).localeCompare(
            b.userName ?? b.email,
          );
          if (byUser !== 0) {
            return byUser;
          }

          const byWorkspace = a.workspaceName.localeCompare(b.workspaceName);
          if (byWorkspace !== 0) {
            return byWorkspace;
          }

          return a.roleName.localeCompare(b.roleName);
        });

        const orderedRoles = [...result.roles].sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        this.users.set(orderedUsers);
        this.roles.set(orderedRoles);
        this.selectedLinkRoleId.set(this.findDefaultLinkRoleId(orderedRoles));
      });
  }

  private resolveAccountId(): number | null {
    const accountId = this.accountId ?? this.authService.GetAccount();
    return accountId && accountId > 0 ? accountId : null;
  }

  private findDefaultLinkRoleId(roles: RoleLookupModel[]): number | null {
    if (!roles.length) {
      return null;
    }

    const editorRole = roles.find(
      (role) => this.normalizeField(role.name) === 'editor',
    );
    return editorRole?.id ?? roles[0].id;
  }

  private normalizeField(value: string | null | undefined): string {
    return value?.trim().toLocaleLowerCase() ?? '';
  }
}
